import React, { useState } from 'react'
import {
  Button, Typography, Link, Box, Collapse,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import LinkIcon from '@mui/icons-material/Link'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import TableChartIcon from '@mui/icons-material/TableChart'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import ArticleIcon from '@mui/icons-material/Article'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import CodeIcon from '@mui/icons-material/Code'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { formatFileSize, formatDate } from '../utils'
import Markdown from './Markdown'

// Maps a file's extension or MIME type to an icon + accent color, so the
// resource list reads at a glance instead of being a wall of identical rows.
function iconFor({ name, mimeType, type }) {
  if (type === 'url') return { Icon: LinkIcon, color: '#1565c0' }
  const ext = (name || '').split('.').pop().toLowerCase()
  const mt = (mimeType || '').toLowerCase()
  if (ext === 'pdf' || mt.includes('pdf')) {
    return { Icon: PictureAsPdfIcon, color: '#d32f2f' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic'].includes(ext)
      || mt.startsWith('image/')) {
    return { Icon: ImageIcon, color: '#43a047' }
  }
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)
      || mt.includes('spreadsheet') || mt.includes('excel') || mt.includes('csv')) {
    return { Icon: TableChartIcon, color: '#2e7d32' }
  }
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)
      || mt.includes('presentation') || mt.includes('powerpoint')) {
    return { Icon: SlideshowIcon, color: '#ef6c00' }
  }
  if (['doc', 'docx', 'odt', 'pages', 'rtf', 'txt', 'md'].includes(ext)
      || mt.includes('word') || mt.startsWith('text/')) {
    return { Icon: ArticleIcon, color: '#1565c0' }
  }
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext)
      || mt.startsWith('video/')) {
    return { Icon: VideoFileIcon, color: '#6a1b9a' }
  }
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext)
      || mt.startsWith('audio/')) {
    return { Icon: AudioFileIcon, color: '#ad1457' }
  }
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)
      || mt.includes('zip') || mt.includes('compressed')) {
    return { Icon: FolderZipIcon, color: '#6d4c41' }
  }
  if (['stl', 'obj', '3mf', 'fbx', 'gltf', 'glb', 'step', 'stp', 'ply', 'dae', '3ds'].includes(ext)
      || mt.includes('stl') || mt.includes('model/')) {
    return { Icon: ViewInArIcon, color: '#00838f' }
  }
  if (['json', 'xml', 'yml', 'yaml', 'js', 'ts', 'rb', 'py', 'sh', 'html', 'css'].includes(ext)) {
    return { Icon: CodeIcon, color: '#37474f' }
  }
  return { Icon: InsertDriveFileIcon, color: '#5a5d63' }
}

// One resource row: a compact header (icon · name · meta · open/download) plus,
// when the file has documentation, a "Показати опис" toggle and a description
// that expands below on its own line — so it never stretches the header row.
//
// The description is always rendered in the DOM (Collapse keeps it mounted), so
// crawlers index it for SEO even while it stays visually collapsed for visitors.
function ResourceRow({ resource, divider }) {
  const [open, setOpen] = useState(false)
  const isUrl = resource.type === 'url'
  // A link shortcut opens its external URL in a new tab; a file links to its
  // slug-based direct-download path.
  const href = isUrl ? resource.external_url : resource.download_path
  const linkProps = isUrl ? { target: '_blank', rel: 'noopener noreferrer' } : {}
  const updatedAt = formatDate(resource.updated_at)
  // URL shortcuts have no meaningful size, so it's simply absent.
  const meta = [
    formatFileSize(resource.file_size),
    updatedAt && `оновлено ${updatedAt}`,
  ]
    .filter(Boolean)
    .join(' · ')
  const { Icon, color } = iconFor({
    name: resource.name, mimeType: resource.mime_type, type: resource.type,
  })

  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        py: 1.5,
        borderBottom: divider ? '1px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ color, lineHeight: 0, pt: 0.25 }}>
          <Icon sx={{ fontSize: 32 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Link
            href={href}
            underline="hover"
            {...linkProps}
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: 'text.primary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {resource.name}
          </Link>

          <Box
            sx={{
              mt: 0.25,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 1.5,
              rowGap: 0.25,
              color: 'text.secondary',
              fontSize: '0.8125rem',
            }}
          >
            {meta && <Box component="span">{meta}</Box>}
            {resource.documentation && (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                sx={{ fontSize: '0.8125rem', fontWeight: 600 }}
              >
                {open ? 'Згорнути опис' : 'Показати опис'}
              </Link>
            )}
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0, pt: 0.25 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={isUrl ? <OpenInNewIcon /> : <DownloadIcon />}
            component="a"
            href={href}
            {...linkProps}
          >
            {isUrl ? 'Відкрити' : 'Завантажити'}
          </Button>
        </Box>
      </Box>

      {resource.documentation && (
        <Collapse in={open}>
          <Markdown sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
            {resource.documentation}
          </Markdown>
        </Collapse>
      )}
    </Box>
  )
}

// Renders a version's resources. Each one links straight to its slug-based
// direct-download URL — the browser downloads the file on click.
export default function ResourceList({ resources }) {
  if (!resources || resources.length === 0) {
    return <Typography color="text.secondary">У цій версії ще немає ресурсів.</Typography>
  }

  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {resources.map((resource, index) => (
        <ResourceRow
          key={resource.id}
          resource={resource}
          divider={index < resources.length - 1}
        />
      ))}
    </Box>
  )
}
